import { useQuery } from 'react-query';

import { formatNetworth, formatNum, request, useCurrentUserId } from '@/utils';

import { PermissionLevelType } from '../types';
import { useCallback } from 'react';

export function useEffectiveUsdValue() {
  const userId = useCurrentUserId();
  const url = '/user/effective_usd_value';

  const select = useCallback((res: { effective_usd_value: number }) => {
    return formatNetworth(res.effective_usd_value);
  }, []);

  const query = useQuery([url, userId], () => request.get<{ effective_usd_value: number }>(url), {
    select,
  });

  return query;
}

type UsdValueHistoryRes = {
  // [date, usd_value]
  history_usd_values: Array<[string, number]>;
};

export function useUsdValueHistory() {
  const userId = useCurrentUserId();
  const url = '/user/history_usd_values';

  const select = useCallback((res: UsdValueHistoryRes) => {
    const barChartData = res.history_usd_values
      .slice(0, 5)
      .reverse()
      .map(([d, v]) => ({
        value: v,
        _date: d,
        _value: formatNetworth(v),
      }));

    return barChartData;
  }, []);

  const query = useQuery([url, userId], () => request.get<UsdValueHistoryRes>(url), {
    select,
  });

  return query;
}

export function useEffectiveRanking() {
  const userId = useCurrentUserId();
  const url = '/user/effective_rank';

  const select = useCallback((res: { effective_rank: number }) => {
    return formatNum(res.effective_rank);
  }, []);

  const query = useQuery([url, userId], () => request.get<{ effective_rank: number }>(url), {
    select,
  });

  return query;
}

export function useRankHistory() {
  const userId = useCurrentUserId();
  const url = '/user/history_rank_list';

  const select = useCallback((res: { history_rank_list: Array<[string, number]> }) => {
    const data = res.history_rank_list.slice(0, 5).reverse();
    const values = data.map(x => x[1]);

    const maxValue = Math.max.apply(null, values);
    const minValue = Math.min.apply(null, values);
    const drift = maxValue + (maxValue - minValue) * 5;

    return data.map(([d, v]) => ({
      // rank 值越小越高
      value: drift - v,
      _date: d,
      _value: formatNum(v),
    }));
  }, []);

  const query = useQuery(
    [url, userId],
    () =>
      request.get<{
        // [date, rank]
        history_rank_list: Array<[string, number]>;
      }>(url),
    {
      select,
    },
  );

  return query;
}

type PermissionLevel = {
  total_count: number;
  type: PermissionLevelType;
  used_count: number;
  wait_seconds: number;
};

export function usePermissionLevels() {
  const userId = useCurrentUserId();
  const url = '/user/check_permission_level_list';

  const select = useCallback((res: { usages: PermissionLevel[] }) => {
    return res.usages.map(x => ({
      ...x,
      _totalCount: formatNum(x.total_count),
      _usedCount: formatNum(x.used_count),
    }));
  }, []);

  const query = useQuery([url, userId], () => request.get<{ usages: PermissionLevel[] }>(url), {
    select,
  });

  return query;
}
